import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import css from './MediaUpload.module.css';
import { Icons, IconSpinner } from '../../components';
import { isArrayLength } from '../../util/genericHelpers';
import { handleDeleteFile } from '../../util/mediaHelpers';
import { showErrorToast } from '../../util/toast';
import { MediaFieldName, PhotoTag, UserType } from '../../util/enums';
import MenuContainer from './MenuContainer';
import { useDispatch, useSelector } from 'react-redux';
import { quickUpdateImages, uploadImage } from '../../ducks/global.duck';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { urlToFile } from '../../util/helperFunction';
import { updateProfile } from '../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import { useConfiguration } from '../../context/configurationContext';
import { uploadImageInProgressSelector } from '../../containers/EditListingPage/EditListingPage.duck';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB

// Helper to create a unique identifier for a file
const getFileIdentifier = file => {
  if (file?.id?.uuid) {
    return `uuid-${file.id.uuid}`;
  }
  if (file.fileLink) {
    return `link-${file.fileLink}`;
  }
  // For File objects, use a combination of name and size for uniqueness
  return `file-${file.name}-${file.size}-${file.lastModified || ''}`;
};

const MediaUpload = ({
  form,
  fieldName,
  acceptedTypes,
  multiple,
  heading,
  description,
  userType,
  values,
}) => {
  const {
    profileImageIndex = null,
    tradingCardImageIndex = null,
    profileImage = {},
    tradingCardImage = {},
  } = values || {};
  const [menuVisibility, setMenuVisibility] = useState({});

  const dispatch = useDispatch();
  const config = useConfiguration();
  const params = useParams();
  const uploadInProgress = useSelector(uploadImageInProgressSelector);
  // Use formFiles as single source of truth
  const formFiles = form.getState().values[fieldName] || [];

  const onDrop = async acceptedFiles => {
    if (!multiple && formFiles.length > 0) {
      showErrorToast(`Only one ${multiple ? 'file' : 'image'} can be uploaded.`);
      return;
    }

    const oversizedFiles = acceptedFiles.filter(file => file.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      showErrorToast(`File size should not exceed 20 MB.`);
      return;
    }

    // Prevent duplicate files using the unique identifier
    const newFiles = acceptedFiles.filter(
      file =>
        !formFiles.some(existingFile => {
          const newId = getFileIdentifier(file);
          const existingId = getFileIdentifier(existingFile);
          return newId === existingId;
        })
    );

    if (newFiles.length === 0) return;

    const combinedFiles = [...formFiles, ...newFiles];
    form.change(fieldName, combinedFiles);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: acceptedTypes,
    multiple,
  });

  const handleTagImage = async (index, type) => {
    if (index !== 0 && formFiles.length > index && type === PhotoTag.PROFILE) {
      const taggedFile = formFiles[index];
      const newFiles = [taggedFile, ...formFiles.filter((_, i) => i !== index)];
      form.change(fieldName, newFiles);

      const hasValidIds = newFiles.every(file => file.id?.uuid);
      if (isArrayLength(newFiles) && hasValidIds) {
        dispatch(
          quickUpdateImages({
            id: params?.id,
            images: newFiles.map(file => file.id.uuid),
            isProfilePictures: true,
            config,
          })
        );
        const filename = `downloaded_${taggedFile.id.uuid}.jpg`;
        const file = await urlToFile(taggedFile.fileLink, filename);
        const response = await dispatch(uploadImage({ image: file }));
        const uploadedId = response?.data?.id;
        dispatch(updateProfile({ profileImageId: uploadedId }));
      }
    }

    if (type === PhotoTag.PROFILE) {
      form.change('profileImageIndex', 0);
    } else if (type === PhotoTag.TRADING_CARD) {
      form.change('tradingCardImageIndex', index);
    }

    setMenuVisibility(prev => ({ ...prev, [index]: false }));
  };

  const toggleMenu = index => {
    setMenuVisibility(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handleDelete = async (fileToDelete, index) => {
    // Simply filter out the file at the given index
    const filteredFiles = formFiles.filter((_, idx) => idx !== index);

    // Only dispatch API calls if we have files with valid IDs
    const ids = filteredFiles.map(file => file?.id?.uuid).filter(Boolean);
    if (ids.length > 0 || filteredFiles.length === 0) {
      await dispatch(
        quickUpdateImages({
          id: params?.id,
          images: ids,
          isProfilePictures: true,
          config,
        })
      );
    }
    // Update form state first
    form.change(fieldName, filteredFiles);
    if (fileToDelete?.key) {
      // aws s3
      handleDeleteFile({
        file: fileToDelete,
        form,
        fieldName,
      });
    }
  };

  const renderFilePreview = (file, index) => {
    const fileUrl = file.fileLink || (file instanceof File ? URL.createObjectURL(file) : '');
    const fileType = file.type || '';
    const isProfileTagged = profileImageIndex === index || profileImage?.index === index;
    const isTradingCardTagged =
      tradingCardImageIndex === index || tradingCardImage?.index === index;
    const isMenuOpen = menuVisibility[index];

    const { PROFILE_VIDEOS } = MediaFieldName;

    return (
      <>
        {fileType.startsWith('video/') || fieldName === PROFILE_VIDEOS ? (
          <video controls className={css.uploadedFile} onLoad={() => URL.revokeObjectURL(file)}>
            <source src={fileUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        ) : (
          <img
            src={fileUrl}
            alt="Preview"
            className={css.uploadedFile}
            onLoad={() => URL.revokeObjectURL(file)}
          />
        )}
        {userType !== UserType.TALENT && (
          <span onClick={() => handleDelete(file, index)} className={css.delete}>
            ×
          </span>
        )}
        {userType === UserType.TALENT && (
          <MenuContainer
            userType={userType}
            form={form}
            file={file}
            fieldName={fieldName}
            index={index}
            isMenuOpen={isMenuOpen}
            isProfileTagged={isProfileTagged}
            isTradingCardTagged={isTradingCardTagged}
            handleTagImage={handleTagImage}
            handleDeleteFile={() => handleDelete(file, index)}
            toggleMenu={toggleMenu}
            profileImageIndex={profileImageIndex}
            tradingCardImageIndex={tradingCardImageIndex}
            setMenuVisibility={setMenuVisibility}
            profileImage={profileImage}
            tradingCardImage={tradingCardImage}
          />
        )}
      </>
    );
  };

  const showUploadSection = multiple || formFiles.length === 0;

  return (
    <div className={css.mediaUploadSection}>
      <label>{heading}</label>
      {showUploadSection && (
        <div className={css.dropArea} {...getRootProps()} style={dropzoneStyle}>
          <input {...getInputProps()} />
          <div className={css.uploadCloud}>
            <Icons name="upload" />
          </div>
          <p>
            <span>{heading}</span> <br />
            {description}
          </p>
        </div>
      )}
      {isArrayLength(formFiles) ? (
        <div className={css.uploadedFiles}>
          {formFiles.map((file, index) => {
            const uniqueKey = getFileIdentifier(file);
            return (
              <div key={uniqueKey} className={css.uploadedFile}>
                {renderFilePreview(file, index)}
              </div>
            );
          })}
        </div>
      ) : null}
      {uploadInProgress && (
        <div className={classNames(css.uploadOverlay, css.fadeIn)}>
          <div className={css.uploadContainer}>
            <div className={css.spinnerWrapper}>
              <IconSpinner className={css.spinner} aria-label="Uploading files" />
            </div>
            <p className={css.uploadText}>
              <FormattedMessage id="MediaUpload.uploading" />
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const dropzoneStyle = {
  border: '2px dashed #ccc',
  borderRadius: '4px',
  padding: '20px',
  textAlign: 'center',
  cursor: 'pointer',
};

export default MediaUpload;
